import React, { ChangeEvent, ClipboardEvent, ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { usePasteEvent } from '../../../hooks';
import { getFilteredClasses } from '../../../utils';
import { ITextarea } from './types';
import styles from './styles.module.scss';

const Textarea = forwardRef(({ children, className, error, onChange, onPaste, onPasteChange, sanitize, value, ...rest }: ITextarea, ref: ForwardedRef<HTMLTextAreaElement>) => {
  const [controlledValue, setControlledValue] = useState(children || value);
  const filteredClasses = getFilteredClasses([className, styles.textarea, error ? 'border-danger' : '']);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const handlePasteEvent = usePasteEvent(textareaRef);
  useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const sanitizedInput = DOMPurify.sanitize(event.target.value);
    const sanitizedEvent = { ...event, target: { ...event.target, value: sanitizedInput } };
    const { xevent, xinput } = sanitize ? { xevent: sanitizedEvent, xinput: sanitizedInput } : { xevent: event, xinput: event.target.value };
    setControlledValue(xinput);

    if (onChange) {
      onChange(xevent);
    }

    return xevent;
  };

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    event.preventDefault();
    const newVal = handlePasteEvent({ event });
    const updatedValue = sanitize ? DOMPurify.sanitize(newVal) : newVal;
    setControlledValue(updatedValue);

    // expose the complete value of the textarea when pasting (similar to onChange)
    if (onPasteChange) {
      onPasteChange(updatedValue);
    }

    if (onPaste) {
      onPaste(event);
    }

    return event;
  };

  useEffect(() => {
    setControlledValue(value);
  }, [value]);

  return (
    <textarea className={filteredClasses} data-testid="textarea" onChange={handleChange} onPaste={handlePaste} ref={textareaRef} value={controlledValue?.toString()} {...rest}>
      {controlledValue}
    </textarea>
  );
});

export { Textarea };
